import { createApi } from '@reduxjs/toolkit/query/react';
import { urls } from '../../utils/helpers/ApiClient';
import axiosFetchBase from '../axiosFetchBase';

export const ingProjectAssignmentsApi = createApi({
  reducerPath: 'ingProjectAssignmentsApi',
  keepUnusedDataFor: 0,
  baseQuery: axiosFetchBase,
  tagTypes: ['AssignmentMatrix', 'AssignmentStatus'],
  endpoints: (builder) => ({
    getAssignmentStatus: builder.query({
      query: () => ({
        url: urls.ingProjectsAssignmentsStatus,
        method: 'GET',
      }),
      providesTags: ['AssignmentStatus'],
    }),
    autoAssignStudents: builder.mutation({
      query: ({ studyProgramId }) => ({
        url: urls.ingProjectsAssignmentsAssign,
        method: 'POST',
        data: { study_program_id: studyProgramId },
      }),
      invalidatesTags: ['AssignmentMatrix', 'AssignmentStatus'],
    }),
    unassignStudents: builder.mutation({
      query: ({ studyProgramId }) => ({
        url: urls.ingProjectsAssignmentsUnassign,
        method: 'POST',
        data: { study_program_id: studyProgramId },
      }),
      invalidatesTags: ['AssignmentMatrix', 'AssignmentStatus'],
    }),
    getAssignmentMatrix: builder.query({
      query: ({ studyProgramId }) => ({
        url: urls.ingProjectsAssignmentsMatrix,
        method: 'GET',
        params: { study_program_id: studyProgramId },
      }),
      providesTags: ['AssignmentMatrix'],
    }),
    manualAssignStudent: builder.mutation({
      async onQueryStarted(
        { studentId, ipProjectId, studyProgramId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          ingProjectAssignmentsApi.util.updateQueryData(
            'getAssignmentMatrix',
            { studyProgramId },
            (draft) => {
              const matrix = draft?.data || draft;
              if (!matrix) return;
              const student = (matrix.students || []).find((item) => item.student_id === studentId);

              const assignmentKey = `${studentId}-${ipProjectId}`;
              const existingAssignment = (matrix.assignments || []).some(
                (assignment) => `${assignment.student_id}-${assignment.ip_project_id}` === assignmentKey,
              );

              if (!existingAssignment) {
                matrix.assignments = [
                  ...(matrix.assignments || []),
                  {
                    student_id: studentId,
                    ip_project_id: ipProjectId,
                    study_program_id: studyProgramId,
                    study_program_shortcut: matrix.study_program_shortcut || null,
                  },
                ];
              }

              if (Array.isArray(matrix.project_assigned_students)) {
                const existingTooltipAssignment = matrix.project_assigned_students.some(
                  (assignment) => `${assignment.student_id}-${assignment.ip_project_id}` === assignmentKey,
                );

                if (!existingTooltipAssignment) {
                  matrix.project_assigned_students = [
                    ...matrix.project_assigned_students,
                    {
                      student_id: studentId,
                      ip_project_id: ipProjectId,
                      study_program_id: studyProgramId,
                      study_program_shortcut: matrix.study_program_shortcut || null,
                      student_name: student?.name || String(studentId),
                    },
                  ];
                }
              }

              if (matrix.project_stats?.[ipProjectId]) {
                const previousStats = matrix.project_stats[ipProjectId];
                const nextAssigned = Math.max((previousStats.assigned || 0) + 1, 0);
                const nextAssignedInStudyProgram = Math.max((previousStats.assigned_in_study_program || 0) + 1, 0);
                matrix.project_stats[ipProjectId] = {
                  ...previousStats,
                  assigned: nextAssigned,
                  assigned_in_study_program: nextAssignedInStudyProgram,
                  assigned_in_other_study_programs: Math.max(nextAssigned - nextAssignedInStudyProgram, 0),
                };
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ studentId, ipProjectId, studyProgramId }) => ({
        url: urls.ingProjectsAssignmentsManualAssign,
        method: 'POST',
        data: {
          student_id: studentId,
          ip_project_id: ipProjectId,
          study_program_id: studyProgramId,
        },
      }),
      invalidatesTags: ['AssignmentMatrix', 'AssignmentStatus'],
    }),
    manualUnassignStudent: builder.mutation({
      async onQueryStarted(
        { studentId, ipProjectId, studyProgramId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          ingProjectAssignmentsApi.util.updateQueryData(
            'getAssignmentMatrix',
            { studyProgramId },
            (draft) => {
              const matrix = draft?.data || draft;
              if (!matrix) return;

              const assignmentKey = `${studentId}-${ipProjectId}`;
              matrix.assignments = (matrix.assignments || []).filter(
                (assignment) => `${assignment.student_id}-${assignment.ip_project_id}` !== assignmentKey,
              );

               if (Array.isArray(matrix.project_assigned_students)) {
                matrix.project_assigned_students = matrix.project_assigned_students.filter(
                  (assignment) => `${assignment.student_id}-${assignment.ip_project_id}` !== assignmentKey,
                );
              }

              if (matrix.project_stats?.[ipProjectId]) {
                const previousStats = matrix.project_stats[ipProjectId];
                const nextAssigned = Math.max((previousStats.assigned || 0) - 1, 0);
                const nextAssignedInStudyProgram = Math.max((previousStats.assigned_in_study_program || 0) - 1, 0);
                matrix.project_stats[ipProjectId] = {
                  ...previousStats,
                  assigned: nextAssigned,
                  assigned_in_study_program: nextAssignedInStudyProgram,
                  assigned_in_other_study_programs: Math.max(nextAssigned - nextAssignedInStudyProgram, 0),
                };
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ studentId, ipProjectId, studyProgramId }) => ({
        url: urls.ingProjectsAssignmentsManualUnassign,
        method: 'POST',
        data: {
          student_id: studentId,
          ip_project_id: ipProjectId,
          study_program_id: studyProgramId,
        },
      }),
      invalidatesTags: ['AssignmentMatrix', 'AssignmentStatus'],
    }),
    publishAssignment: builder.mutation({
      query: ({ studyProgramId, notifyStudents = false }) => ({
        url: urls.ingProjectsAssignmentsPublish,
        method: 'POST',
        data: {
          study_program_id: studyProgramId,
          notify_students: notifyStudents,
        },
      }),
      invalidatesTags: ['AssignmentMatrix', 'AssignmentStatus'],
    }),
  }),
});

export const {
  useGetAssignmentStatusQuery,
  useAutoAssignStudentsMutation,
  useUnassignStudentsMutation,
  useGetAssignmentMatrixQuery,
  useManualAssignStudentMutation,
  useManualUnassignStudentMutation,
  usePublishAssignmentMutation,
} = ingProjectAssignmentsApi;
